import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '../../hooks/usePermissions'
import { fichaService } from '../../services/fichaService'
import { mascotaService } from '../../services/mascotaService'
import MobileCard, { MobileCardContent } from '../../components/ui/MobileCard'
import Select from '../../components/ui/Select'
import Modal, { ModalHeader, ModalContent } from '../../components/ui/Modal'
import Button from '../../components/ui/Button'
import { FileText, Calendar, Heart, Building2, Stethoscope, Download, Activity, Pill, Sparkles } from 'lucide-react'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import { normalizeFotoUrl } from '../../utils/avatarHelpers'

function extractArray(payload) {
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload?.data)) return payload.data
  if (Array.isArray(payload?.data?.data)) return payload.data.data
  return []
}

export default function FichasMobile() {
  const navigate = useNavigate()
  const { isDueno } = usePermissions()
  const [fichas, setFichas] = useState([])
  const [mascotas, setMascotas] = useState([])
  const [selectedMascota, setSelectedMascota] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadingMascotas, setLoadingMascotas] = useState(true)
  const [selectedFicha, setSelectedFicha] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [tratamientos, setTratamientos] = useState([])
  const [seguimientos, setSeguimientos] = useState([])
  const [loadingFichaDetail, setLoadingFichaDetail] = useState(false)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    fetchFichas()
    fetchMascotas()
  }, [isDueno])

  useEffect(() => {
    // No necesitamos recargar las fichas cuando cambia el filtro,
    // solo las filtramos en el cliente
  }, [selectedMascota])

  const fetchFichas = async () => {
    try {
      setLoading(true)
      // Obtener todas las fichas clínicas
      const response = await fichaService.getAll()
      setFichas(extractArray(response))
    } catch (error) {
      console.error('Error fetching fichas:', error)
      setFichas([])
    } finally {
      setLoading(false)
    }
  }

  const fetchMascotas = async () => {
    try {
      setLoadingMascotas(true)
      let response
      if (isDueno) {
        response = await mascotaService.getMascotasByDueno()
      } else {
        response = await mascotaService.getAll({ per_page: 100 })
      }
      setMascotas(extractArray(response))
    } catch (error) {
      console.error('Error fetching mascotas:', error)
      setMascotas([])
    } finally {
      setLoadingMascotas(false)
    }
  }

  // Filtrar fichas por mascota seleccionada
  const filteredFichas = selectedMascota
    ? fichas.filter(ficha => {
        const mascotaId = ficha.mascota?.id_mascota || ficha.mascota?.idMascota || ficha.id_mascota || ficha.idMascota
        return mascotaId && mascotaId.toString() === selectedMascota.toString()
      })
    : fichas

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible'
    const date = new Date(dateString)
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Fecha no disponible'
    const date = new Date(dateString)
    return date.toLocaleString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleFichaClick = async (ficha) => {
    setSelectedFicha(ficha)
    setIsModalOpen(true)
    setLoadingFichaDetail(true)
    
    try {
      const fichaId = ficha.id_ficha || ficha.idFicha
      const [tratamientosResponse, seguimientosResponse] = await Promise.all([
        fichaService.getTratamientos(fichaId),
        fichaService.getSeguimientos(fichaId)
      ])
      setTratamientos(extractArray(tratamientosResponse))
      setSeguimientos(extractArray(seguimientosResponse))
    } catch (error) {
      console.error('Error fetching ficha details:', error)
    } finally {
      setLoadingFichaDetail(false)
    }
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedFicha(null)
    setTratamientos([])
    setSeguimientos([])
  }

  const handleDownloadPDF = async () => {
    if (!selectedFicha) return
    try {
      setDownloading(true)
      const fichaId = selectedFicha.id_ficha || selectedFicha.idFicha
      await fichaService.descargarPDF(fichaId)
    } catch (error) {
      console.error('Error al descargar PDF:', error)
      alert(error.message || 'Error al descargar el PDF')
    } finally {
      setDownloading(false)
    }
  }

  const getEstadoColor = (estado) => {
    if (!estado) return 'bg-gray-100 text-gray-700'
    switch (estado.toUpperCase()) {
      case 'CERRADO':
      case 'RESUELTO':
        return 'bg-green-100 text-green-700'
      case 'EN_TRATAMIENTO':
      case 'EN TRATAMIENTO':
        return 'bg-blue-100 text-blue-700'
      case 'URGENTE':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-yellow-100 text-yellow-700'
    }
  }

  if (loading || loadingMascotas) {
    return <LoadingState message="Cargando fichas..." />
  }

  if (filteredFichas.length === 0) {
    return (
      <div className="min-h-full bg-transparent pb-4">
        {/* Filtro de mascotas */}
        <div className="px-4 pt-4 pb-4">
          <div className="rounded-3xl border border-pastel-pink bg-white/95 p-4 shadow-soft">
          <Select
            value={selectedMascota}
            onChange={(e) => setSelectedMascota(e.target.value)}
            leftIcon={Heart}
            className="w-full"
          >
            <option value="">Todas las mascotas</option>
            {mascotas.map((mascota) => (
              <option key={mascota.id_mascota || mascota.idMascota} value={mascota.id_mascota || mascota.idMascota}>
                {mascota.nombre || 'Sin nombre'}
              </option>
            ))}
          </Select>
          </div>
        </div>
        <div className="px-4 py-6">
          <EmptyState
            icon={FileText}
            title="No hay fichas clínicas"
            message={selectedMascota ? "No hay fichas clínicas para esta mascota" : "Aún no tienes fichas clínicas registradas"}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-transparent pb-4">
      {/* Filtro de mascotas */}
      <div className="px-4 pt-4 pb-4 sticky top-0 z-10">
        <div className="rounded-3xl border border-pastel-pink bg-white/95 p-4 shadow-soft backdrop-blur-sm">
          <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold mb-2">Medical records</p>
          <Select
            value={selectedMascota}
            onChange={(e) => setSelectedMascota(e.target.value)}
            leftIcon={Heart}
            className="w-full"
          >
            <option value="">Todas las mascotas</option>
            {mascotas.map((mascota) => (
              <option key={mascota.id_mascota || mascota.idMascota} value={mascota.id_mascota || mascota.idMascota}>
                {mascota.nombre || 'Sin nombre'}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Lista de fichas */}
      <div className="px-4 pb-6 space-y-4">
        {filteredFichas.map((ficha) => {
          const mascotaFoto = ficha.mascota?.foto || ficha.mascota?.avatar_url
          const getMascotaImage = () => {
            if (mascotaFoto) {
              return normalizeFotoUrl(mascotaFoto)
            }
            return '/placeholder-pet.svg'
          }

          const estadoCaso = ficha.estado_caso || ficha.estadoCaso || 'PENDIENTE'
          const getEstadoColor = () => {
            switch (estadoCaso.toUpperCase()) {
              case 'CERRADO':
              case 'RESUELTO':
                return 'bg-green-100 text-green-700'
              case 'EN_TRATAMIENTO':
              case 'EN TRATAMIENTO':
                return 'bg-blue-100 text-blue-700'
              case 'URGENTE':
                return 'bg-red-100 text-red-700'
              default:
                return 'bg-yellow-100 text-yellow-700'
            }
          }

          return (
            <MobileCard
              key={ficha.id_ficha || ficha.idFicha}
              onClick={() => handleFichaClick(ficha)}
              className="cursor-pointer active:scale-[0.98] overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Layout moderno mejorado */}
              <div className="flex flex-row items-stretch">
                {/* Imagen de la mascota con gradiente moderno - usa height completo */}
                <div className="w-28 md:w-36 flex-shrink-0 bg-gradient-to-br from-pastel-teal/20 via-pastel-peach/30 to-pastel-lavender/20 flex items-center justify-center relative overflow-hidden self-stretch">
                  {/* Efecto decorativo */}
                  <div className="absolute top-2 right-2 opacity-20">
                    <Sparkles className="w-6 h-6 text-pastel-teal" />
                  </div>
                  <img
                    src={getMascotaImage()}
                    alt={ficha.mascota?.nombre || 'Mascota'}
                    className="w-full h-full object-cover relative z-10"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextElementSibling.style.display = 'flex'
                    }}
                  />
                  <div className="hidden w-full h-full items-center justify-center relative z-10">
                    <Heart className="w-10 h-10 text-pastel-teal" />
                  </div>
                </div>

                {/* Contenido mejorado */}
                <MobileCardContent className="p-4 flex-1 flex flex-col justify-between">
                  <div className="flex-1">
                    {/* Header con badge de estado flotante */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0 pr-2">
                        <div className="flex items-center space-x-2 mb-1">
                          <div className="p-1.5 bg-pastel-teal/20 rounded-lg">
                            <FileText className="w-3.5 h-3.5 text-pastel-ink" />
                          </div>
                          <h3 className="font-bold text-gray-900 text-base truncate">
                            {ficha.mascota?.nombre || 'Mascota'}
                          </h3>
                        </div>
                        {ficha.mascota?.especie && (
                          <p className="text-xs text-gray-500 mb-2 font-medium">
                            {ficha.mascota.especie}
                            {ficha.mascota.raza && ` • ${ficha.mascota.raza}`}
                          </p>
                        )}
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 shadow-sm ${getEstadoColor()}`}>
                        {estadoCaso}
                      </span>
                    </div>

                    {/* Diagnóstico con mejor presentación */}
                    {ficha.diagnostico && (
                      <div className="mb-3 p-2.5 bg-pastel-cream rounded-xl">
                        <p className="text-xs font-medium text-gray-500 mb-1">Diagnóstico</p>
                        <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
                          {ficha.diagnostico}
                        </p>
                      </div>
                    )}

                    {/* Información adicional con iconos mejorados */}
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2 text-xs text-gray-600">
                        <div className="p-1 bg-pastel-teal/10 rounded">
                          <Calendar className="w-3 h-3 text-pastel-ink" />
                        </div>
                        <span className="font-medium">{formatDate(ficha.fecha || ficha.fecha_creacion || ficha.fechaCreacion)}</span>
                      </div>
                      {ficha.veterinario && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="p-1 bg-blue-50 rounded">
                            <Stethoscope className="w-3 h-3 text-blue-600" />
                          </div>
                          <span className="truncate font-medium">
                            Dr. {ficha.veterinario.nombre || ''} {ficha.veterinario.apellido || ''}
                          </span>
                        </div>
                      )}
                      {ficha.clinica && (
                        <div className="flex items-center space-x-2 text-xs text-gray-600">
                          <div className="p-1 bg-purple-50 rounded">
                            <Building2 className="w-3 h-3 text-purple-600" />
                          </div>
                          <span className="truncate font-medium">{ficha.clinica.nombre || ficha.clinica.nombreClinica}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </MobileCardContent>
              </div>
            </MobileCard>
          )
        })}
      </div>

      {/* Modal de detalle de ficha */}
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="full">
        {selectedFicha && (
          <>
            <ModalHeader onClose={handleCloseModal}>
              Ficha Clínica
            </ModalHeader>
            <ModalContent>
              {loadingFichaDetail ? (
                <div className="py-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-teal mx-auto"></div>
                  <p className="mt-2 text-sm text-gray-500">Cargando detalles...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Header con imagen de mascota */}
                  <div className="relative -mx-4 -mt-4 mb-6">
                    <div className="relative w-full h-48 bg-gradient-to-br from-pastel-teal/20 via-pastel-peach/30 to-pastel-lavender/20 overflow-hidden">
                      {/* Efectos decorativos */}
                      <div className="absolute inset-0 opacity-20">
                        <div className="absolute top-4 left-4">
                          <Sparkles className="w-8 h-8 text-pastel-teal" />
                        </div>
                        <div className="absolute top-8 right-8">
                          <Sparkles className="w-6 h-6 text-blue-400" />
                        </div>
                        <div className="absolute bottom-6 left-1/2">
                          <Sparkles className="w-7 h-7 text-pastel-teal" />
                        </div>
                      </div>
                      <div className="relative h-full flex items-center justify-center p-6">
                        {(() => {
                          const mascotaFoto = selectedFicha.mascota?.foto || selectedFicha.mascota?.avatar_url
                          const getMascotaImage = () => {
                            if (mascotaFoto) {
                              return normalizeFotoUrl(mascotaFoto)
                            }
                            return '/placeholder-pet.svg'
                          }
                          return (
                            <img
                              src={getMascotaImage()}
                              alt={selectedFicha.mascota?.nombre || 'Mascota'}
                              className="h-full w-auto object-contain rounded-2xl"
                              onError={(e) => {
                                e.target.style.display = 'none'
                                e.target.nextElementSibling.style.display = 'flex'
                              }}
                            />
                          )
                        })()}
                        <div className="hidden w-full h-full items-center justify-center">
                          <Heart className="w-20 h-20 text-pastel-teal" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Nombre y estado flotante */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="bg-white rounded-2xl p-4 shadow-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h2 className="text-xl font-bold text-gray-900 mb-1">
                              {selectedFicha.mascota?.nombre || 'Mascota'}
                            </h2>
                            <p className="text-sm text-gray-600">
                              {selectedFicha.mascota?.especie || ''}
                              {selectedFicha.mascota?.raza && ` • ${selectedFicha.mascota.raza}`}
                            </p>
                          </div>
                          {(selectedFicha.estado_caso || selectedFicha.estadoCaso) && (
                            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm ${getEstadoColor(selectedFicha.estado_caso || selectedFicha.estadoCaso)}`}>
                              {selectedFicha.estado_caso || selectedFicha.estadoCaso}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Información General con diseño moderno */}
                  <div className="grid grid-cols-1 gap-3">
                    <div className="flex items-center space-x-3 p-3 bg-pastel-cream rounded-2xl">
                      <div className="p-2 bg-pastel-teal/20 rounded-xl">
                        <Calendar className="w-5 h-5 text-pastel-ink" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 font-medium">Fecha</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {formatDate(selectedFicha.fecha || selectedFicha.fecha_creacion || selectedFicha.fechaCreacion)}
                        </p>
                      </div>
                    </div>

                    {selectedFicha.veterinario && (
                      <div className="flex items-center space-x-3 p-3 bg-pastel-cream rounded-2xl">
                        <div className="p-2 bg-blue-100 rounded-xl">
                          <Stethoscope className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium">Veterinario</p>
                          <p className="text-sm font-semibold text-gray-900">
                            Dr. {selectedFicha.veterinario.nombre || ''} {selectedFicha.veterinario.apellido || ''}
                          </p>
                        </div>
                      </div>
                    )}

                    {selectedFicha.clinica && (
                      <div className="flex items-center space-x-3 p-3 bg-pastel-cream rounded-2xl">
                        <div className="p-2 bg-purple-100 rounded-xl">
                          <Building2 className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium">Clínica</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {selectedFicha.clinica.nombre || selectedFicha.clinica.nombreClinica}
                          </p>
                        </div>
                      </div>
                    )}

                    {(selectedFicha.numero_ficha || selectedFicha.numeroFicha) && (
                      <div className="flex items-center space-x-3 p-3 bg-pastel-cream rounded-2xl">
                        <div className="p-2 bg-orange-100 rounded-xl">
                          <FileText className="w-5 h-5 text-orange-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 font-medium">Número de Ficha</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {selectedFicha.numero_ficha || selectedFicha.numeroFicha}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Diagnóstico */}
                  {selectedFicha.diagnostico && (
                    <div className="p-4 bg-pastel-teal/10 rounded-2xl border border-pastel-teal/40">
                      <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center">
                        <FileText className="w-4 h-4 mr-2 text-pastel-ink" />
                        Diagnóstico
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {selectedFicha.diagnostico}
                      </p>
                    </div>
                  )}

                  {/* Observaciones */}
                  {selectedFicha.observaciones && (
                    <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100">
                      <h3 className="text-base font-bold text-gray-900 mb-2 flex items-center">
                        <Activity className="w-4 h-4 mr-2 text-blue-600" />
                        Observaciones
                      </h3>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {selectedFicha.observaciones}
                      </p>
                    </div>
                  )}

                  {/* Tratamientos */}
                  {tratamientos.length > 0 && (
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                        <Pill className="w-5 h-5 mr-2 text-pastel-ink" />
                        Tratamientos
                      </h3>
                      <div className="space-y-3">
                        {tratamientos.map((tratamiento, index) => (
                          <MobileCard key={index} className="bg-white border border-gray-200">
                            <MobileCardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-bold text-gray-900 text-sm">
                                  {tratamiento.medicamento || tratamiento.nombre || 'Tratamiento'}
                                </h4>
                                {tratamiento.fecha && (
                                  <span className="text-xs text-gray-500 font-medium">
                                    {formatDate(tratamiento.fecha)}
                                  </span>
                                )}
                              </div>
                              {tratamiento.dosis && (
                                <p className="text-sm text-gray-700 mb-1">
                                  <span className="font-semibold">Dosis:</span> {tratamiento.dosis}
                                </p>
                              )}
                              {tratamiento.frecuencia && (
                                <p className="text-sm text-gray-700 mb-1">
                                  <span className="font-semibold">Frecuencia:</span> {tratamiento.frecuencia}
                                </p>
                              )}
                              {tratamiento.observaciones && (
                                <p className="text-sm text-gray-600 mt-2 pt-2 border-t border-gray-100">
                                  {tratamiento.observaciones}
                                </p>
                              )}
                            </MobileCardContent>
                          </MobileCard>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Seguimientos */}
                  {seguimientos.length > 0 && (
                    <div>
                      <h3 className="text-base font-bold text-gray-900 mb-3 flex items-center">
                        <Activity className="w-5 h-5 mr-2 text-pastel-ink" />
                        Seguimientos
                      </h3>
                      <div className="space-y-3">
                        {seguimientos.map((seguimiento, index) => (
                          <MobileCard key={index} className="bg-white border border-gray-200">
                            <MobileCardContent className="p-4">
                              <div className="flex items-start justify-between mb-2">
                                <h4 className="font-bold text-gray-900 text-sm">
                                  Seguimiento #{index + 1}
                                </h4>
                                {seguimiento.fecha && (
                                  <span className="text-xs text-gray-500 font-medium">
                                    {formatDateTime(seguimiento.fecha)}
                                  </span>
                                )}
                              </div>
                              {seguimiento.observaciones && (
                                <p className="text-sm text-gray-700 mb-2">
                                  {seguimiento.observaciones}
                                </p>
                              )}
                              {seguimiento.estado && (
                                <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${getEstadoColor(seguimiento.estado)}`}>
                                  {seguimiento.estado}
                                </span>
                              )}
                            </MobileCardContent>
                          </MobileCard>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Botón Descargar PDF */}
                  <div className="pt-4 border-t border-gray-200">
                    <Button
                      onClick={() => {
                        const fichaId = selectedFicha.id_ficha || selectedFicha.idFicha
                        if (fichaId) navigate(`/fichas/${fichaId}`)
                      }}
                      variant="secondary"
                      className="w-full mb-2"
                    >
                      Ver pantalla completa
                    </Button>
                    <Button
                      onClick={handleDownloadPDF}
                      disabled={downloading}
                      variant="primary"
                      className="w-full"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {downloading ? 'Descargando...' : 'Descargar PDF'}
                    </Button>
                  </div>
                </div>
              )}
            </ModalContent>
          </>
        )}
      </Modal>
    </div>
  )
}
